import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "motion/react";

export default function SettingsButton({ onClick, children, ...props }) {
  const buttonRef = useRef();
  const measurements = useRef();

  useEffect(() => {
    measureButton();
  }, []);

  const [measured, setMeasured] = useState(false);
  const [tapping, setTapping] = useState(false);
  const [hovering, setHovering] = useState(false);

  const hoverStart = (e) => {
    if (!measurements.current) measureButton();
    setHovering(true);
  };
  const hoverEnd = (e) => {
    setHovering(false);
  };

  const shineColor = "#FFFFFF";

  const measureButton = () => {
    measurements.current = buttonRef.current.getBoundingClientRect();
    setMeasured(true);
  };

  const updatePointer = ({ offsetX, offsetY }) => {
    buttonRef.current.style.setProperty(
      "--fnrButtonShineX",
      (offsetX / measurements.current.width) * 100 + "%"
    );
    buttonRef.current.style.setProperty(
      "--fnrButtonShineY",
      (offsetY / measurements.current.height) * 100 + "%"
    );
  };

  useEffect(() => {
    measureButton();
    const button = buttonRef.current;
    button.addEventListener("pointermove", updatePointer);
    button.addEventListener("mouseover", measureButton);
    return () => {
      button.removeEventListener("pointermove", updatePointer);
      button.removeEventListener("mouseover", measureButton);
    };
  }, []);

  const tapStart = () => setTapping(true);
  const tapEnd = () => setTapping(false);

  const styles = {
    "--fnrButtonShineX": "50%",
    "--fnrButtonShineY": "50%",
    "--fnrButtonShineColor": shineColor,
  };

  if (measured) {
    styles["--fnrButtonShineRadius"] =
      Math.max(80, measurements.current.width, measurements.current.height) *
        2 +
      "px";
  }

  const style = { ...styles };

  const handleClick = (e) => {
    if (onClick) onClick();
  };

  return (
    <FnrButtonWrapper
      ref={buttonRef}
      onHoverStart={hoverStart}
      onHoverEnd={hoverEnd}
      onTapStart={tapStart}
      onTapCancel={tapEnd}
      onTap={tapEnd}
      onClick={handleClick}
      style={style}
      {...props}
    >
      <Inside>
        <Shine />
        <ContentAround>
          <FnrButtonContent
            className="FnrButtonContent"
            animate={{
              scale: tapping ? 0.9 : 1,
            }}
          >
            {children}
          </FnrButtonContent>
        </ContentAround>
      </Inside>
    </FnrButtonWrapper>
  );
}

const FnrButtonWrapper = styled(motion.div)`
  border-radius: 24px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  appearance: none;
  border: none;
  transition: filter 0.2s;
`;
const Inside = styled.div`
  border-radius: 24px;
  display: flex;
  height: 100%;
  align-items: center;
  position: relative;
  z-index: 5;
  overflow: hidden;
  transition: box-shadow 1.2s;
  box-shadow: inset rgba(255, 255, 255, 0) 0 0 1px;

  ${FnrButtonWrapper}:hover & {
    box-shadow: inset rgba(255, 255, 255, 0.3) 0 0 3px;
    transition: box-shadow 0.2s;
  }

  &::before {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    opacity: 0;
    background: radial-gradient(
      calc(var(--fnrButtonShineRadius) * 0.8) circle at 28% 20%,
      var(--fnrButtonHilightColor),
      transparent 40%
    );
    /* dumb old safari */
    @supports not (aspect-ratio: auto) {
      background: radial-gradient(
        calc(var(--fnrButtonShineRadius) * 4) circle at 28% 20%,
        var(--fnrButtonHilightColor),
        transparent 40%
      );
    }
    z-index: -1;
  }
`;
const FnrButtonContent = styled(motion.div)`
  color: #fff;
  font-weight: 400;
  position: relative;
  z-index: 20;
  user-select: none;
  text-shadow: rgba(0, 0, 0, 0.2) 1px 1px 2px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;

  &::after {
    position: absolute;
    content: "";
    z-index: 100;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0);
  }
`;
const ContentAround = styled.div`
  z-index: 40;
  position: relative;
  width: 100%;
`;

const Shine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: radial-gradient(
    var(--fnrButtonShineRadius) circle at var(--fnrButtonShineX)
      var(--fnrButtonShineY),
    var(--fnrButtonShineColor),
    transparent 70%
  );
  /* dumb old safari */
  @supports not (aspect-ratio: auto) {
    background: radial-gradient(
      calc(var(--fnrButtonShineRadius) * 5) circle at var(--fnrButtonShineX)
        var(--fnrButtonShineY),
      var(--fnrButtonShineColor),
      transparent 40%
    );
  }
  opacity: 0;
  transition:
    opacity 0.8s,
    background 0.2s;
  z-index: 3;

  ${FnrButtonWrapper}:hover & {
    opacity: 0.2;
    transition:
      opacity 0.4s,
      background 0.2s;
  }
`;
